/**
 * Shared proof domain types and promote-on parsing.
 *
 * @module proof
 * @see docs/project/conventions.md
 */

/**
 * The event that makes a proven workflow eligible for promotion. `never`: the test proves
 * the pull request and is archived at merge instead of joining any suite (proofs-v2 M8).
 */
export type PromoteOn = 'merge' | 'never' | `deploy:${string}`;

/** Parse a stored promote-on value, returning null when it is not supported. */
export function parsePromoteOn(value: string): PromoteOn | null {
  if (value === 'merge' || value === 'never') return value;
  if (!value.startsWith('deploy:')) return null;
  const stage = value.slice('deploy:'.length);
  if (!stage) return null;
  return value as PromoteOn;
}

/** The current evaluation state of a criterion's evidence. */
export type CriterionStatus =
  | { kind: 'proved' }
  | { kind: 'stale'; provedSha: string }
  | { kind: 'failed'; problem: string };

/** The durable evidence kinds accepted by proofs. `manual` is the agent's own account (D11). */
export const PROOF_EVIDENCE_KINDS: readonly ['e2e', 'perf', 'manual'] = ['e2e', 'perf', 'manual'];
export type ProofEvidenceKind = (typeof PROOF_EVIDENCE_KINDS)[number];

/**
 * A criterion shown only by the agent's own account (M23): it has evidence and every item is manual.
 * The one rule behind the check line's 📝, the PR block's `(manual)` and the page's Manual badge.
 */
export const isManualOnly = (kinds: readonly ProofEvidenceKind[]): boolean =>
  kinds.length > 0 && kinds.every((kind) => kind === 'manual');

/** A visual checkpoint comparison shown for a UI test proof. */
export type ProofCheckpoint = {
  label: string;
  nodeId: string;
  status: 'changed' | 'unchanged' | 'incompatible' | 'unavailable' | 'no_base' | 'pending';
  changedPercent: number | null;
  width: number | null;
  height: number | null;
  imageUrl: string | null;
  baselineUrl: string | null;
  diffUrl: string | null;
};

/** Evidence detail shared by API responses, checks, and the proof page. */
export type EvidenceView =
  | {
      kind: 'e2e';
      workflowId: string;
      runId: string;
      nodeId: string | null;
      asserted: string;
      /** An existing approved test cited unchanged: proves it still works, never promoted. */
      existing: boolean;
    }
  | { kind: 'perf'; runId: string; asserted: string }
  | { kind: 'manual'; notes: string; artifacts: string[] };

/** The evaluated result used by both GitHub checks and the Actions job. */
export type ProofVerdict = {
  conclusion: 'success' | 'failure' | 'neutral';
  title: string;
  criteria: {
    position: number;
    text: string;
    detail: string;
    status: CriterionStatus;
    evidence: EvidenceView[];
  }[];
  summaryMarkdown: string;
  /** The proof page for criteria, flows for a criteria block without a record, or null for `none:`. */
  pageUrl: string | null;
};

export type ProofRefusal = 'not-found' | 'approved-not-fork' | 'not-a-draft' | 'origin-unavailable';

/** A test as the server classified it: usable as proof (a new draft, or a fork of an approved test), or refused with why. */
export type ClassifiedProof =
  | { id: string }
  | { id: string; refusal: ProofRefusal; message: string };
